// src/TeamContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const TeamContext = createContext();

function loadFromLocalStorage() {
    try {
        const data = localStorage.getItem("EQUITY_CALC_DATA");
        return data ? JSON.parse(data) : null;
    } catch (err) {
        console.error("Error reading from localStorage", err);
        return null;
    }
}

function saveToLocalStorage(state) {
    try {
        localStorage.setItem("EQUITY_CALC_DATA", JSON.stringify(state));
    } catch (err) {
        console.error("Error writing to localStorage", err);
    }
}

export function TeamProvider({ children }) {
    // Define a default workspace with all required fields.
    const defaultWorkspace = {
        id: 1,
        name: "Default Workspace",
        createdAt: new Date().toISOString(),
        part1Answers: {
            companyName: "",
            foundingTeamSize: 0,
            founderNames: [],
            reservedPools: [],
            foundersCashInvested: {},
            foundersTime: {},
            foundersAssets: {},
            ratingFinancialImpact: 3,
            ratingTimeImportance: 3,
            ratingAssetImportance: 3,
        },
        part2Answers: {
            equityDistributionAdjustment: {},
            coreFactors: [],
            preFormation: [],
            roleResponsibility: [],
            experienceNetworks: [],
        },
        visitedQuestionsPart1: {},
        visitedQuestionsPart2: {},
        isFullQuestionnaireComplete: false,
    };

    const storedData = loadFromLocalStorage();

    // Workspaces: use stored data if available; otherwise default to one workspace.
    const [workspaces, setWorkspaces] = useState(
        storedData?.workspaces || [defaultWorkspace]
    );

    // Active workspace: defaults to workspace id 1.
    const [activeWorkspaceId, setActiveWorkspaceId] = useState(
        storedData?.activeWorkspaceId || 1
    );

    // Show welcome modal (true by default)
    const [showWelcomeModal, setShowWelcomeModal] = useState(
        storedData?.showWelcomeModal !== false
    );

    useEffect(() => {
        const dataToStore = {
            workspaces,
            activeWorkspaceId,
            showWelcomeModal,
        };
        saveToLocalStorage(dataToStore);
    }, [workspaces, activeWorkspaceId, showWelcomeModal]);

    /** Create a new workspace and return it */
    function addWorkspace(name) {
        const newId =
            workspaces.length > 0 ? Math.max(...workspaces.map((w) => w.id)) + 1 : 1;
        const newWs = {
            ...defaultWorkspace,
            id: newId,
            name,
            createdAt: new Date().toISOString(),
            part1Answers: {
                companyName: "",
                foundingTeamSize: 0,
                founderNames: [],
                reservedPools: [],
                foundersCashInvested: {},
                foundersTime: {},
                foundersAssets: {},
                ratingFinancialImpact: 3,
                ratingTimeImportance: 3,
                ratingAssetImportance: 3,
            },
            part2Answers: {
                equityDistributionAdjustment: {},
                coreFactors: [],
                preFormation: [],
                roleResponsibility: [],
                experienceNetworks: [],
            },
            visitedQuestionsPart1: {},
            visitedQuestionsPart2: {},
            isFullQuestionnaireComplete: false,
        };
        setWorkspaces((prev) => [...prev, newWs]);
        return newWs;
    }

    /** Look up a workspace by id */
    function getWorkspaceById(id) {
        return workspaces.find((w) => w.id === id);
    }

    /** Overwrite the workspace data (e.g. after changes) */
    function updateWorkspaceData(wsId, updatedWs) {
        setWorkspaces((prev) =>
            prev.map((w) => (w.id === wsId ? { ...updatedWs } : w))
        );
    }

    /**
     * Calculate the final equity distribution for the active workspace.
     * It uses part1Answers and part2Answers.
     */
    function calculateFinalEquity() {
        const ws = getWorkspaceById(activeWorkspaceId);
        if (!ws) return { finalEquityMap: {}, coreBreakdown: {}, factorAllocations: {} };

        const p1 = ws.part1Answers;
        const p2 = ws.part2Answers;
        const founderNames = p1.founderNames || [];

        // 1. Calculate reserved equity pools
        const reservedEquityTotal = (p1.reservedPools || []).reduce((sum, pool) => sum + (pool.weight || 0), 0);

        // 2. Calculate extra allocations from factors
        const allFactors = [
            ...(p2.coreFactors || []),
            ...(p2.preFormation || []),
            ...(p2.roleResponsibility || []),
            ...(p2.experienceNetworks || [])
        ];

        const founderExtra = {};
        const factorAllocations = {};

        founderNames.forEach(fn => {
            founderExtra[fn] = 0;
            factorAllocations[fn] = {};

            allFactors.forEach(factor => {
                const value = parseFloat(factor.allocations?.[fn] || 0);
                if (!isNaN(value)) {
                    founderExtra[fn] += value;
                    const factorKey = factor.factorName
                        .replace(/[^a-zA-Z]/g, '')
                        .replace(/(?:^\w|[A-Z]|\b\w)/g, (ltr, idx) =>
                            idx === 0 ? ltr.toLowerCase() : ltr.toUpperCase()
                        )
                        .replace(/\s+/g, '');
                    factorAllocations[fn][factorKey] = (factorAllocations[fn][factorKey] || 0) + value;
                }
            });
        });

        // 3. Calculate core equity distribution
        const totalExtra = Object.values(founderExtra).reduce((sum, val) => sum + val, 0);
        const leftoverForFounders = Math.max(100 - reservedEquityTotal - totalExtra, 0);

        // Determine active contribution categories
        const totalCash = Object.values(p1.foundersCashInvested || {}).reduce((a, b) => a + (b || 0), 0);
        const totalTime = Object.values(p1.foundersTime || {}).reduce((a, b) => a + (b || 0), 0);
        const totalAssets = Object.values(p1.foundersAssets || {}).reduce((a, b) => a + (b || 0), 0);

        const hasCash = totalCash > 0;
        const hasTime = totalTime > 0;
        const hasAssets = totalAssets > 0;

        // 4. Calculate weights (manual adjustments or Q7-9 ratings based)
        const manualWeights = p2.equityDistributionAdjustment || {};
        let weights = { financial: 0, time: 0, assets: 0 };

        // Get original ratings from Q7-9 (if category active)
        const R_FI = hasCash ? (p1.ratingFinancialImpact || 0) : 0;
        const R_TC = hasTime ? (p1.ratingTimeImportance || 0) : 0;
        const R_AI = hasAssets ? (p1.ratingAssetImportance || 0) : 0;

        // Determine final weights with manual adjustment priority
        if (Object.keys(manualWeights).length > 0) {
            // Use manual weights where available (filtering inactive categories)
            weights.financial = hasCash ? (manualWeights["Capital Investment (Financial)"] || 0) : 0;
            weights.time = hasTime ? (manualWeights["Time Commitment"] || 0) : 0;
            weights.assets = hasAssets ? (manualWeights["Capital Investment (Assets)"] || 0) : 0;
        } else {
            // Use Q7-9 ratings when no manual adjustments
            weights.financial = R_FI;
            weights.time = R_TC;
            weights.assets = R_AI;
        }

        // Handle zero ratings scenario
        const totalOriginalRatings = R_FI + R_TC + R_AI;
        if (totalOriginalRatings === 0 && !Object.keys(manualWeights).length) {
            // Equal distribution if no ratings and no manual adjustments
            const activeCategories = [hasCash, hasTime, hasAssets].filter(Boolean).length;
            const equalWeight = activeCategories > 0 ? 100 / activeCategories : 0;
            weights.financial = hasCash ? equalWeight : 0;
            weights.time = hasTime ? equalWeight : 0;
            weights.assets = hasAssets ? equalWeight : 0;
        }

        // Normalize weights
        const totalWeights = weights.financial + weights.time + weights.assets;
        const wFI = totalWeights > 0 ? weights.financial / totalWeights : 0;
        const wTC = totalWeights > 0 ? weights.time / totalWeights : 0;
        const wAI = totalWeights > 0 ? weights.assets / totalWeights : 0;

        // Calculate core distributions
        const coreBreakdown = {};
        const founderTentPerc = {};

        founderNames.forEach(fn => {
            const cash = hasCash ? (p1.foundersCashInvested[fn] || 0) / totalCash : 0;
            const time = hasTime ? (p1.foundersTime[fn] || 0) / totalTime : 0;
            const assets = hasAssets ? (p1.foundersAssets[fn] || 0) / totalAssets : 0;

            const financial = cash * wFI;
            const timeCommit = time * wTC;
            const asset = assets * wAI;
            const totalWeight = financial + timeCommit + asset;

            let corePct = 0;
            if (totalWeight > 0) {
                const effectiveWeightSum = wFI + wTC + wAI;
                corePct = effectiveWeightSum > 0 ?
                    (totalWeight / effectiveWeightSum) * leftoverForFounders :
                    0;
            }

            founderTentPerc[fn] = corePct;

            coreBreakdown[fn] = {
                financial: totalWeight > 0 ? (financial / totalWeight) * corePct : 0,
                time: totalWeight > 0 ? (timeCommit / totalWeight) * corePct : 0,
                assets: totalWeight > 0 ? (asset / totalWeight) * corePct : 0
            };
        });

        // 5. Final equity allocation
        const finalEquityMap = {};
        founderNames.forEach(fn => {
            finalEquityMap[fn] = founderTentPerc[fn] + founderExtra[fn];
        });

        return {
            finalEquityMap,
            reservedEquityTotal,
            leftoverForFounders,
            coreBreakdown,
            factorAllocations
        };
    }

// Helper function to get factor values
    function getFactorValue(factors, founder, factorName) {
        if (!factors) return 0;
        const factor = factors.find(f => f.factorName === factorName);
        return factor ? parseFloat(factor.allocations[founder] || 0) : 0;
    }

    return (
        <TeamContext.Provider
            value={{
                workspaces,
                setWorkspaces,
                activeWorkspaceId,
                setActiveWorkspaceId,
                showWelcomeModal,
                setShowWelcomeModal,
                addWorkspace,
                getWorkspaceById,
                updateWorkspaceData,
                calculateFinalEquity,
            }}
        >
            {children}
        </TeamContext.Provider>
    );
}

export function useTeamContext() {
    return useContext(TeamContext);
}
